import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { CheckCircle, XCircle } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { InvestReviewerInvite } from '@/common/types/invitation';
import { inviteService } from '@/services/inviteService';
import { authService } from '@/services/authService';

import { toastUtils } from '@/common/utils/toastUtils';
import { extractDate } from '@/common/utils/dateFormat';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { semanticColors } from '@/common/styles/colorTokens';

// Common Components
import { InvitationLayout } from './layout/InvitationLayout';
import Section from '@/common/components/section/Section';
import { Button } from '@/common/components/button/Button';
import Avatar from '@/common/components/avatar/Avatar';
import { LabelInput } from '@/common/components/input/LabelInput';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { useNavigationStore } from '@/common/store';
import { getBirthGender } from '@/common/utils/commonUtil';

// Add validation types
export interface ValidationErrors {
  loginId?: string;
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
  password?: string;
  passwordConfirm?: string;
}

const InvitationInvestReviewPage: React.FC = () => {
  const [ searchParams ] = useSearchParams();
  const {  } = useNavigationStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  
  const [inviteInfo, setInviteInfo] = useState<InvestReviewerInvite | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [showSignupForm, setShowSignupForm] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [certifyData, setCertifyData] = useState<InisisAuthResult | null>(null);
  const [differentUser, setDifferentUser] = useState<boolean>(false);

  const [formData, setFormData] = useState({
    loginId: '',
    birthGender: '',
    name: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirm: '',
    company: '',
    userDi: '',
    userCi: '',
    token: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const { 
    openPopup,  
    error 
  } = usePopupLogin({
    onSuccess: async (data) => {
      try {
        // 사용자 정보 확인
        const responseUser = await authService.setUserProfile();
        
        if(!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }
        if(!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        // 이름과 이메일 확인
        const isNameMatch = responseUser.data.name === inviteInfo.content?.name;
        const isEmailMatch = responseUser.data.email === inviteInfo.content?.email || 
                            responseUser.data.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.content?.email);

        if (!isNameMatch || !isEmailMatch) {
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        // 초대 수락 API 호출
        const registeCompanyUserResult = await inviteService.acceptInviteForLogin(notificationId!);
        if(registeCompanyUserResult?.success) {
          toastUtils.success('수락 완료');
          getInviteInfo();
        } else {
          toastUtils.error('수락 실패');
        }
      } catch (error) {
        console.error('초대 수락 실패:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      }
    },
    onError: (err) => {
      console.error('로그인 실패:', err);
    }
  });

  // Validation rules
  const validateField = useCallback((name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return '이름을 입력해주세요';
        if (value.length < 2) return '이름은 2자 이상이어야 합니다';
        return '';

      case 'phone':
        if (!value) return '휴대폰 번호를 입력해주세요';
        const phoneRegex = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;
        if (!phoneRegex.test(value)) return '올바른 휴대폰 번호 형식이 아닙니다';
        return '';

      case 'email':
        if (!value) return '이메일을 입력해주세요';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return '올바른 이메일 형식이 아닙니다';
        return '';

      case 'company':
        if (!value.trim()) return '소속을 입력해주세요';
        return '';

      case 'password':
        if (!value) return '비밀번호를 입력해주세요';
        if (value.length < 4) return '비밀번호는 4자 이상이어야 합니다';
        return '';

      case 'passwordConfirm':
        if (!value) return '비밀번호 확인을 입력해주세요';
        if (value !== formData.password) return '비밀번호가 일치하지 않습니다';
        return '';

      default:
        return '';
    }
  }, [formData.password]);

  const handleAcceptInvitation = async () => {
    // TODO: Implement API call to accept invitation
    console.log('초대 수락 API 호출');
  };

  const handleRejectInvitation = async () => {
    if (!rejectReason.trim()) {
      toastUtils.error('거절 사유를 입력해주세요.');
      return;
    }
    
    try {
      // 실제 거절 API 호출
      const response = await inviteService.rejectInvite({
        notificationId: notificationId!,
        reason: rejectReason
      });

      if (response?.success) {
        setShowRejectModal(false);
        setDifferentUser(false); // differentUser 상태도 초기화
        toastUtils.success('초대가 성공적으로 거절되었습니다.');
        // 거절 완료 화면을 보여주기 위해 초대 정보 다시 로드
        getInviteInfo();
      } else {
        throw new Error('거절 처리 실패');
      }
    } catch (error) {
      console.error('초대 거절 실패:', error);
      toastUtils.error('초대 거절 중 오류가 발생했습니다.');
    }
  };

  const handleSignupComplete = async () => {
    const isValid = validateForm();

    if (!isValid) return;

    if(!isCertifiedPersonal) {
      toastUtils.error('간편 인증을 진행해주세요.');
      return;
    }

    try {
      const response = await inviteService.acceptInviteForRegister({
        loginId: formData.loginId,
        residentNumber: formData.birthGender+'******',
        name: formData.name, 
        phone: formData.phone, 
        email: formData.email, 
        password: formData.password, 
        company: formData.company, 
        notificationId: notificationId!, 
        emailVerified: true, 
        userDi: formData.userDi, 
        userCi: formData.userCi,
        token: formData.token
      });
      
      if(response?.success) {
        toastUtils.success('초대 수락 완료');
        getInviteInfo();
      } else {
        toastUtils.error('초대 수락 실패');
      }
    } catch (error) {
      console.error('회원가입 실패:', error);
    }
  };

  // Modified handleInputChange
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate on change if field was touched
    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  // Handle blur event for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, value)
    }));
  };

  // Validate all fields
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};
    Object.keys(formData).forEach(key => {
      newErrors[key as keyof ValidationErrors] = validateField(key, formData[key as keyof typeof formData]);
    });
    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  }, [formData, validateField]);

  // 간편인증 처리 함수 추가
  const handleCertify = async (data: InisisAuthResult) => {
    console.log('인증 데이터:', data);
    
    if (data.isDuplicate) {
      setIsCertifiedPersonal(false);
      return toastUtils.error('이미 가입된 유저입니다.');
    }
    
    toastUtils.success('간편인증이 완료되었습니다. 나머지 정보를 입력해주세요.');
    setShowSignupForm(true);
    setIsCertifiedPersonal(true);
    setCertifyData(data);
    setFormData(prev => ({
      ...prev, 
      name: data.userName, 
      phone: data.userPhone,
      birthGender: getBirthGender(data.userBirth, data.userGender),
      userDi: data.userDi, 
      userCi: data.userCi,
      token: data.token
    }));
  };

  const getInviteInfo = async() => {
    const notificationId = searchParams.get('n_id');
    if(!notificationId) return;
    setNotificationId(notificationId);
    const response = await inviteService.getInviteInfoForInvestReviewer(notificationId);
    setInviteInfo(response);
  }

  useEffect(() => {
    
    getInviteInfo();
  }, []);

  if(!inviteInfo) return <LoadingContainer><div>초대 정보를 로드 중입니다...</div></LoadingContainer>;

  // 완료된 화면
  if (inviteInfo.isApproved !== undefined) {
    if (inviteInfo.isApproved) {
      return (
        <InvitationLayout maxWidth="800px">
          <WelcomeContent>
            <SuccessAnimation>
              <CheckCircle size={64} color="#22C55E" />
            </SuccessAnimation>
            <Section
              title="초대 수락 완료"
              description="투자 검토위원으로 초대가 수락되었습니다."
            >
              <CompletedInfo>
                <InfoRow>
                  <InfoLabel>회사명</InfoLabel>
                  <InfoValue>{inviteInfo.content?.companyName}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>역할</InfoLabel>
                  <InfoValue>{inviteInfo.content?.role}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>초대 일정</InfoLabel>
                  <InfoValue>{extractDate(inviteInfo.content?.date)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>수락 일시</InfoLabel>
                  <InfoValue>{new Date().toLocaleDateString('ko-KR')} {new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</InfoValue>
                </InfoRow>
              </CompletedInfo>
              <Button 
                $variant='fill' 
                onClick={() => navigate('/')} 
                $isFull
                style={{ marginTop: '24px' }}
              >
                메인 페이지로 이동
              </Button>
            </Section>
          </WelcomeContent>
        </InvitationLayout>
      );
    }

    return (
      <InvitationLayout maxWidth="600px">
        <WelcomeContent>
          <SuccessAnimation>
            <XCircle size={64} color="#DC3545" />
          </SuccessAnimation>
          <Section
            title="초대 거절 완료"
            description="투자 검토를 거절했습니다."
          >
            <RejectedInfo>
              <InfoRow>
                <InfoLabel>회사명</InfoLabel>
                <InfoValue>{inviteInfo.content?.companyName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>초대자</InfoLabel>
                <InfoValue>{inviteInfo.content?.name}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>거절 일시</InfoLabel>
                <InfoValue>{new Date().toLocaleDateString('ko-KR')} {new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</InfoValue>
              </InfoRow>
            </RejectedInfo>
            
            <RejectedNotice>
              <p>거절 사유가 초대자에게 전달되었습니다.</p>
              <p>추후 동일한 회사에서 다시 초대를 받을 수 있습니다.</p>
            </RejectedNotice>

            <Button 
              $variant='fill' 
              onClick={() => navigate('/')} 
              $isFull
              style={{ marginTop: '24px' }}
            >
              메인 페이지로 이동
            </Button>
          </Section>
        </WelcomeContent>
      </InvitationLayout>
    );
  }

  // 초기 화면
  return (
    <InvitationLayout maxWidth="600px">
      <Section
        title={`${inviteInfo.inviter.companyName} 투자심의 심사위원 초대`}
        description={`${inviteInfo.inviter.name}님이 ${inviteInfo.content?.name}님을 ${inviteInfo.inviter.companyName}의 투자심의 외부위원으로 초대했습니다.`}
      >
        {/* 초대자 정보 */}
        <InfoSection>
          <SectionTitle>초대자 정보</SectionTitle>
          <InviterProfile>
            <Avatar
              alt={inviteInfo.content?.name || '초대자'}
              sizes="md"
            />
            <ProfileInfo>
              <ProfileName>{inviteInfo.inviter.name}</ProfileName>
              <ProfilePosition>{inviteInfo.inviter.companyName}</ProfilePosition>
              <ProfileEmail>{inviteInfo.inviter.email}</ProfileEmail>
            </ProfileInfo>
          </InviterProfile>
        </InfoSection>

        {/* 초대 내용 */}
        <InfoSection>
          <SectionTitle>초대 내용</SectionTitle>
          <InfoList>
            <InfoItem>
              <InfoLabel>회사명</InfoLabel>
              <InfoValue>{inviteInfo.content?.companyName}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>역할</InfoLabel>
              <InfoValue>{inviteInfo.content?.role}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>초대 일정</InfoLabel>
              <InfoValue>{extractDate(inviteInfo.content?.date)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>초대 이메일</InfoLabel>
              <InfoValue>{inviteInfo.content?.email}</InfoValue>
            </InfoItem>
          </InfoList>
        </InfoSection>

        {/* differentUser 상태일 때 거절 사유 입력 */}
        {differentUser ? (
          <>
            <AlertBox $variant="error">
              <XCircle size={20} />
              <AlertText>
                초대된 사용자와 다른 사용자입니다. 자동적으로 거절 처리됩니다.
                <br />
                초대 정보를 확인하시고 틀린 정보가 있다면 자세한 사유를 작성해주세요.
                <small>
                  * 사유는 초대자에게 전달됩니다.
                </small>
              </AlertText>
            </AlertBox>
            
            <FormItem>
              <textarea
                style={{
                  width: '100%', 
                  height: 120, 
                  padding: 16, 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  fontSize: '14px'
                }}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요"
              />
            </FormItem>

            <ButtonGroup>
              <Button onClick={() => setDifferentUser(false)} $variant='outline' $size='sm'>
                다시하기
              </Button>
              <Button 
                disabled={!rejectReason.trim()} 
                onClick={handleRejectInvitation}
                $size='sm'
              >
                거절하기
              </Button>
            </ButtonGroup>
          </>
        ) : (
          <>
            {/* 회원가입 폼 */}
            {showSignupForm && (
              <InfoSection>
                <SectionTitle>개인 정보 입력</SectionTitle>
                {!isCertifiedPersonal ? (
                  <div>
                    <p style={{ color: semanticColors.text.tertiary, marginBottom: '16px' }}>
                      간편 인증을 먼저 진행해주세요.
                    </p>
                  </div>
                ) : (
                  <FormSection>
                    <HighlightInfo>
                      <HighlightItem>
                        <HighlightLabel>이름</HighlightLabel>
                        <HighlightValue>{formData.name}</HighlightValue>
                      </HighlightItem>
                      <HighlightItem>
                        <HighlightLabel>전화번호</HighlightLabel>
                        <HighlightValue>{formData.phone}</HighlightValue>
                      </HighlightItem>
                    </HighlightInfo>

                    <FormGroup>
                      <LabelInput
                        label="아이디"
                        type="text"
                        value={formData.loginId}
                        onChange={(e) => {
                          handleInputChange(e);
                          setTouched(prev => ({ ...prev, loginId: true }));
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <LabelInput
                        label="이메일"
                        type="text"
                        value={formData.email}
                        onChange={(e) => {
                          handleInputChange(e);
                          setTouched(prev => ({ ...prev, email: true }));
                        }}
                        onBlur={(e) => {
                          handleBlur(e);
                          // 입력된 이메일이 초대된 이메일과 다르면 자동으로 거절 처리
                          if (e.target.value && inviteInfo?.content?.email && e.target.value !== inviteInfo.content.email) {
                            toastUtils.error('초대받은 이메일과 다른 이메일입니다. 거절 사유를 입력해주세요.');
                            setRejectReason('초대받은 이메일과 다른 이메일이어서 거절합니다.');
                            setShowRejectModal(true);
                          }
                        }}
                        placeholder="이메일을 입력해주세요"
                        error={touched.email && !!errors.email}
                        errorMessage={errors.email}
                      />
                    </FormGroup>

                    <FormGroup>
                      <LabelInput
                        label="소속"
                        type="text"
                        value={formData.company}
                        onChange={(e) => {
                          handleInputChange(e);
                          setTouched(prev => ({ ...prev, company: true }));
                        }}
                        onBlur={handleBlur}
                        placeholder="소속을 입력해주세요"
                        error={touched.company && !!errors.company}
                        errorMessage={errors.company}
                      />
                    </FormGroup>

                    <FormGroup>
                      <LabelInput
                        label="비밀번호"
                        type="password"
                        value={formData.password}
                        onChange={(e) => {
                          handleInputChange(e);
                          setTouched(prev => ({ ...prev, password: true }));
                        }}
                        onBlur={handleBlur}
                        placeholder="비밀번호를 입력해주세요"
                        error={touched.password && !!errors.password}
                        errorMessage={errors.password}
                      />
                    </FormGroup>

                    <FormGroup>
                      <LabelInput
                        label="비밀번호 확인"
                        type="password"
                        value={formData.passwordConfirm}
                        onChange={(e) => {
                          handleInputChange(e);
                          setTouched(prev => ({ ...prev, passwordConfirm: true }));
                        }}
                        onBlur={handleBlur}
                        placeholder="비밀번호 확인을 입력해주세요"
                        error={touched.passwordConfirm && !!errors.passwordConfirm}
                        errorMessage={errors.passwordConfirm}
                      />
                    </FormGroup>
                  </FormSection>
                )}
              </InfoSection>
            )}

            {/* 버튼 그룹 */}
            <ButtonGroup>
              {!showSignupForm ? (
                <>
                  <InisisAuthButton 
                    $variant='outline' 
                    $size='sm' 
                    $title='회원가입 후 수락' 
                    onSuccess={handleCertify}
                  />
                  <Button $variant='fill' $size='sm' onClick={openPopup}>
                    로그인 후 수락
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    $variant='outline' 
                    $size='sm'
                    onClick={() => setShowRejectModal(true)}
                  >
                    거절하기
                  </Button>
                  <Button 
                    $variant='fill' 
                    $size='sm'
                    disabled={!isCertifiedPersonal || Object.values(errors).some(error => error !== '')} 
                    onClick={handleSignupComplete}
                  >
                    완료하기
                  </Button>
                </>
              )}
            </ButtonGroup>
          </>
        )}

        {error && (
          <ErrorMessage>
            {error.message}
          </ErrorMessage>
        )}
      </Section>

      {/* Reject Modal */}
      <ModalCenter
        isOpen={showRejectModal}
        title="투자 검토 거절"
        maxWidth="500px"
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        footer={
          <ButtonGroup style={{justifyContent: 'flex-end'}}>
            <Button 
              $variant='outline' 
              $size='sm'
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
            >
              취소
            </Button>
            <Button 
              $variant='fill' 
              $size='sm'
              disabled={!rejectReason.trim()} 
              onClick={handleRejectInvitation}
            >
              거절하기
            </Button>
          </ButtonGroup>
        }
      >
        <RejectModalContent>
          <RejectDescription>
            투자 검토를 거절하는 사유를 입력해주세요.<br />
            작성하신 내용은 초대자에게 전달됩니다.
          </RejectDescription>
          
          <FormGroup>
            <RejectReasonLabel>거절 사유</RejectReasonLabel>
            <RejectReasonTextarea
              value={rejectReason}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 300) {
                  setRejectReason(value);
                }
              }}
              placeholder="거절 사유를 자세히 입력해주세요."
              rows={4}
              maxLength={300}
            />
            <CharacterCount $isNearLimit={rejectReason.length > 200}>
              {rejectReason.length}/300자
            </CharacterCount>
          </FormGroup>
        </RejectModalContent>
      </ModalCenter>
    </InvitationLayout>
  );
};

// Styled Components - InvitationPitchingPage.tsx와 동일한 스타일 적용
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: ${semanticColors.text.tertiary};
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
`;

const SuccessAnimation = styled.div`
  margin: 0 auto 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: ${semanticColors.background.secondary};
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const InfoSection = styled.div`
  background: ${semanticColors.background.secondary};
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${semanticColors.border.primary};
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${semanticColors.text.primary};
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  
  span:first-child {
    width: 80px;
    color: ${semanticColors.text.tertiary};
    flex-shrink: 0;
  }
  
  span:last-child {
    color: ${semanticColors.text.primary};
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InfoLabel = styled.span`
  color: ${semanticColors.text.tertiary};
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: ${semanticColors.text.primary};
  font-weight: 500;
  font-size: 14px;
`;

const InviterProfile = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
`;

const ProfileName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${semanticColors.text.primary};
  margin-bottom: 4px;
`;

const ProfilePosition = styled.div`
  font-size: 14px;
  color: ${semanticColors.text.tertiary};
  margin-bottom: 4px;
`;

const ProfileEmail = styled.div`
  font-size: 14px;
  color: ${semanticColors.text.tertiary};
`;

const FormSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const HighlightInfo = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HighlightItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HighlightLabel = styled.span`
  font-size: 12px;
  color: ${semanticColors.text.tertiary};
  text-transform: uppercase;
`;

const HighlightValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${semanticColors.text.primary};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CompletedInfo = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  min-width: 450px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${semanticColors.border.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${semanticColors.text.onDanger.secondary};
  margin-top: 16px;
  font-size: 14px;
  text-align: center;
`;

const RejectModalContent = styled.div`
  padding: 24px;
`;

const RejectDescription = styled.div`
  margin-bottom: 24px;
  color: ${semanticColors.text.primary};
  font-size: 14px;
  text-align: center;
  line-height: 1.6;
`;

const RejectReasonLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${semanticColors.text.primary};
  font-size: 14px;
`;

const RejectReasonTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  background-color: ${semanticColors.background.secondary};
  color: ${semanticColors.text.primary};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${semanticColors.background.brand};
    box-shadow: 0 0 0 2px ${semanticColors.background.brand}20;
  }

  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
`;

const CharacterCount = styled.span<{ $isNearLimit: boolean }>`
  display: block;
  margin-top: 8px;
  color: ${props => props.$isNearLimit ? semanticColors.text.onDanger.primary : semanticColors.text.tertiary};
  font-size: 12px;
`;

const RejectedInfo = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const RejectedNotice = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  
  p {
    margin: 0;
    color: ${semanticColors.text.secondary};
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
    
    &:first-child {
      margin-bottom: 12px;
      font-weight: 500;
    }
    
    &:last-child {
      color: ${semanticColors.text.tertiary};
    }
  }
`;

const AlertBox = styled.div<{ $variant: string }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  background-color: ${props => 
    props.$variant === 'error' ? semanticColors.background.danger.secondary : 
    props.$variant === 'warning' ? semanticColors.background.warning.secondary : 
    props.$variant === 'success' ? semanticColors.background.success.secondary : 
    semanticColors.background.info.secondary};
  border: 1px solid ${props => 
    props.$variant === 'error' ? semanticColors.border.dangerSecondary : 
    props.$variant === 'warning' ? semanticColors.border.warning : 
    props.$variant === 'success' ? semanticColors.border.success : 
    semanticColors.border.info};
  color: ${props => 
    props.$variant === 'error' ? semanticColors.text.primary : 
    props.$variant === 'warning' ? semanticColors.text.primary : 
    props.$variant === 'success' ? semanticColors.text.primary : 
    semanticColors.text.primary};
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  
  small {
    display: block;
    margin-top: 0.5rem;
    opacity: 0.8;
  }
`;

const FormItem = styled.div`
  margin-bottom: 0.375rem;
`;

export default InvitationInvestReviewPage;